import React from 'react'

import { styled, Typography, Grid } from '@mui/material'

import { DefaultCompanyCardLayout } from './DefaultCompanyCardLayout'
import { CompanyInformationWidget } from './CompanyInformationWidget'
import { CompanyLocationsWidget } from './CompanyLocationsWidget'
import { Panel } from '../Panel'
import { CompanyAgentCardsWidget } from './CompanyAgentCardsWidget'

import { getActiveDashboardId, type DashboardLayout, type DashboardCardDef } from '@edge/spark/io-dashboards'
import type { GetCompanyByIdQuery } from '@edge/spark/io-dashboards'

const StyledHeader = styled(Typography)`
    margin: 1.5rem 1rem 1rem 0;
`

const updateCardTitle = (ss: string, field: string, value: string): string => {
    const fieldUpdate = new RegExp(`<company.name.id>`, 'g')
    return fieldUpdate.test(ss) ? ss.replace(fieldUpdate, `${value}`) : ss
}

const updateSearchString = (ss: string, field: string, value: string): string => {
    const fieldUpdate = new RegExp(`(<(${field})>):(<[a-zA-Z0-9-{}.:_]{1,}>)`, 'g')
    return fieldUpdate.test(ss) ? ss.replace(fieldUpdate, `$2:${value}`) : ss
}

const StyledGrid = styled(Grid)(({ theme }) => ({
    '&:nth-child(1)': {
        order: 1,
    },
    '&:nth-child(2)': {
        order: 3,
        [theme.breakpoints.down('md')]: {
            order: 2,
        },
    },
    '&:nth-child(3)': {
        order: 2,
        [theme.breakpoints.down('md')]: {
            order: 3,
        },
    },
    '&:nth-child(4)': {
        order: 4,
    },
}))

interface Props {
    accountId: number
    data?: GetCompanyByIdQuery
    loading?: boolean
    companyData?: any
}

export const CompanyInformationTab: React.FC<Props> = ({ accountId, data, loading, companyData }: Props) => {
    const { displayName, accountKey } = data?.auction?.accounts?.byId || {}

    const encodedDisplayName = displayName?.replaceAll('"', '\\"') || ''
    const dataMap = new Map<string, string>()
    dataMap.set('company.name.id', `${encodedDisplayName}${accountKey ? ` (${accountKey})` : ''}`)
    dataMap.set('selleraccountid', String(accountId))
    dataMap.set('buyeraccountid', String(accountId))

    const getActiveDashboard = (
        replacementMap: Map<string, string>,
        dashboards: Array<DashboardLayout>,
    ): DashboardLayout => {
        const activeDashboardId = getActiveDashboardId()
        const db = dashboards.find((d) => d.id === activeDashboardId) || DefaultCompanyCardLayout

        const cards: Array<DashboardCardDef> = []

        db.cards.forEach((card) => {
            if (card.config?.search?.searchString) {
                let ss = JSON.stringify(card)
                replacementMap.forEach((value: string, key: string) => {
                    ss = updateCardTitle(ss, key, value)
                    ss = updateSearchString(ss, key, value)
                })

                const updatedCard = JSON.parse(ss) as DashboardCardDef

                cards.push(updatedCard)
            }
        })

        const activeDB: DashboardLayout = { ...db }
        activeDB.cards = cards
        return activeDB
    }

    const activeDashboard = getActiveDashboard(dataMap, [DefaultCompanyCardLayout])

    if (!data) {
        return null
    }

    return (
        <Grid container spacing={2} justifyContent="space-evenly">
            <StyledGrid item xs={12} md={8}>
                <StyledHeader variant="h5">Overview Stats</StyledHeader>
                <Panel dashboardLayout={activeDashboard} />
            </StyledGrid>
            <StyledGrid item xs={12} md={8}>
                <StyledHeader variant="h5">Information</StyledHeader>
                <CompanyInformationWidget data={data} loading={loading} />
            </StyledGrid>
            <StyledGrid item xs={12} md={4}>
                <StyledHeader variant="h5">Locations</StyledHeader>
                <CompanyLocationsWidget data={data} />
            </StyledGrid>
            <StyledGrid item xs={12} md={4}>
                <StyledHeader variant="h5">Agents</StyledHeader>
                <CompanyAgentCardsWidget companyData={companyData} data={data} accountId={accountId} />
            </StyledGrid>
        </Grid>
    )
}
